import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { invalidarProcessosMapa } from "@/hooks/useProcessosMapa";
import { AUTOS_META, AUTOS_ORDEM, autosMeta } from "@/components/AutosTag";
import { Info } from "@/components/Dica";

// Lote 118 — "+ Outros autos": cadastra outro autos com o MESMO número (execução provisória, incidente…),
// já com número, partes e cliente copiados do processo em que o advogado está; ou edita a etiqueta/grau dos autos atuais.
// Props: base = processo de partida (com `irmaos`); modo = "novo" | "editar".
export default function AutosDialog({ open, onOpenChange, base, modo = "novo", onSaved }) {
  const usados = new Set([base?.autos || "principal", ...((base?.irmaos || []).map((i) => i.autos))]);
  if (modo === "editar") usados.delete(base?.autos || "principal");
  const livres = AUTOS_ORDEM.filter((k) => k === "outro" || !usados.has(k));
  const [autos, setAutos] = useState("exec_provisoria");
  const [nome, setNome] = useState("");
  const [grau, setGrau] = useState("");
  const [vara, setVara] = useState("");
  const [comarca, setComarca] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !base) return;
    if (modo === "editar") {
      setAutos(base.autos || "principal"); setNome(base.autos_nome || ""); setGrau(base.grau || "");
      setVara(base.vara || ""); setComarca(base.comarca || "");
    } else {
      setAutos(livres.find((k) => k !== "principal") || "outro"); setNome(""); setGrau("");
      setVara(""); setComarca(base.comarca || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modo, base?.processo_id]);

  const salvar = async () => {
    setBusy(true);
    try {
      let r;
      if (modo === "editar") {
        r = await http.put(`/processos/${base.processo_id}`, {
          apelido: base.apelido || null, client_id: base.client_id || null,
          comarca: comarca.trim() || null, vara: vara.trim() || null, case_area: base.case_area || null,
          party_author: base.party_author || null, party_defendant: base.party_defendant || null,
          valor_causa: base.valor_causa || null, data_ajuizamento: base.data_ajuizamento || null,
          autos, autos_nome: autos === "outro" ? nome.trim() || null : null, grau: grau || null,
        });
        toast.success("Etiqueta atualizada");
      } else {
        r = await http.post("/processos", {
          numero: base.numero, client_id: base.client_id || null,
          comarca: comarca.trim() || null, vara: vara.trim() || null, case_area: base.case_area || null,
          party_author: base.party_author || null, party_defendant: base.party_defendant || null,
          autos, autos_nome: autos === "outro" ? nome.trim() || null : null, grau: grau || null,
        });
        toast.success("Autos cadastrados e monitorados");
      }
      invalidarProcessosMapa();
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar os autos");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="autos-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{modo === "editar" ? "Etiqueta dos autos" : "Outros autos do mesmo número"}</DialogTitle>
          <DialogDescription className="tabular-nums break-words">{base?.numero}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Etiqueta</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="autos-etiquetas">
              {livres.map((k) => {
                const m = AUTOS_META[k]; const sel = autos === k;
                return (
                  <button key={k} type="button" data-testid={`autos-op-${k}`} aria-pressed={sel} onClick={() => setAutos(k)}
                    className={`inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-full border text-xs font-semibold ${sel ? `${m.cls} ring-2 ring-offset-1 ring-stone-400` : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}>
                    <span className={`w-2 h-2 rounded-full ${m.dot}`} /> {m.label}
                  </button>
                );
              })}
            </div>
            {autos === "outro" && (
              <Input data-testid="autos-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={40}
                placeholder="Nome (ex.: Incidente de desconsideração)" className="mt-2" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Vara / Órgão</Label>
              <Input data-testid="autos-vara" value={vara} onChange={(e) => setVara(e.target.value)} placeholder="Ex: 1ª Vara do Trabalho" />
            </div>
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Comarca</Label>
              <Input data-testid="autos-comarca" value={comarca} onChange={(e) => setComarca(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 flex items-center gap-1.5">
              Grau (opcional)
              <Info titulo="Para que serve o grau">
                Se você disser em que grau estes autos tramitam (1º ou 2º), o app tenta direcionar sozinho os andamentos do DataJud para o autos certo. Sem o grau, os andamentos capturados vão para a fila “A classificar”.
              </Info>
            </Label>
            <div className="flex gap-1.5" data-testid="autos-grau">
              {[["", "Não informado"], ["1", "1º grau"], ["2", "2º grau"]].map(([v, l]) => (
                <button key={v || "x"} type="button" data-testid={`autos-grau-${v || "nd"}`} aria-pressed={grau === v} onClick={() => setGrau(v)}
                  className={`min-h-[36px] px-3 rounded-full border text-xs font-semibold ${grau === v ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          {modo === "novo" && <p className="text-xs text-stone-500">Número, partes e cliente já vêm copiados; ajuste a vara e a comarca se o juízo for outro.</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="autos-salvar" disabled={busy || (autos === "outro" && !nome.trim())} onClick={salvar} className="bg-emerald-brand text-white">
            {busy ? "Salvando…" : modo === "editar" ? "Salvar" : "Cadastrar autos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
